/**
 * Glyphs the P0 screens draw that `@oprappo/ui`'s § 01 icon set does not carry — or
 * carries at different coordinates.
 *
 * This is NOT a fork of `Icon`. Wherever `Icon` has the same path, `Icon` is used; the
 * paths below are the ones the P0 screen frames draw and the design-system icon set does
 * not contain. Each is transcribed verbatim from `Oprappo P0 Screens.dc.html`, exactly
 * as `@oprappo/ui` itself did for the six controls § 01 names but never draws
 * (packages/ui/README.md § "Icons sourced outside the design system").
 *
 * The four EX1 glyphs come from `Oprappo P2 Projects and Explore.dc.html`, which is the
 * only file that draws the unlaunched life domains. B5 needs their names dimmed under
 * the আসছে band and draws only two of the seven; sourcing the other glyphs from EX1 is
 * transcription. Domain 10, ডিজিটাল ও যোগাযোগ, has no glyph in any design file — see
 * `explore/domains.ts`, which reports that rather than inventing one.
 *
 * Every mismatch with `Icon` is listed in the WP-C report:
 *   back arrow   P0 `M15 5l-7 7 7 7`  vs  Icon.chevronLeft  `M14 6l-6 6 6 6`
 *   rowChevron   P0 `M9 6l6 6-6 6`    vs  Icon.chevronRight `M10 6l6 6-6 6`
 *   sendArrow    B1 `M3 12h14…`       vs  Icon.send         `M4 12h14…`
 *
 * The back arrow is deliberately NOT in this set. Every P0 back control is rendered with
 * `IconButton control="back"`, because docs/02 § accessible names fixes its Bangla name
 * (`পিছনে যান`) and `IconButton` is the only thing that guarantees it — § 01: "the name
 * is in the spec, not left to the developer's guess." That costs one pixel of path
 * geometry on nine screens, which is the trade this package chose and reported.
 *
 * This module and `frame.tsx` are the WP-C group's shared primitives. They live under
 * `routes/onboarding/` because that is a directory this work package owns and its grant
 * contains no shared location; a neutral `routes/_shared/` is requested in the report.
 */

export type GlyphName =
  | 'rowChevron'
  | 'clock'
  | 'sort'
  | 'filter'
  | 'sendArrow'
  | 'food'
  | 'service'
  | 'serviceDomain'
  | 'pin'
  | 'education'
  | 'work'
  | 'family'
  | 'government';

export interface GlyphProps {
  name: GlyphName;
  /** Rendered size in px, taken from the frame being built. */
  size: number;
  /** Stroke colour, taken from the frame. */
  stroke: string;
  className?: string | undefined;
}

const SW = 2;

function paths(name: GlyphName, stroke: string): JSX.Element {
  switch (name) {
    // B1, B5, C1 — the row chevron.
    case 'rowChevron':
      return (
        <path d="M9 6l6 6-6 6" stroke={stroke} strokeWidth={SW} strokeLinecap="round" strokeLinejoin="round" />
      );
    // B2 — recent-search rows.
    case 'clock':
      return (
        <>
          <circle cx="12" cy="12" r="9" stroke={stroke} strokeWidth={SW} />
          <path d="M12 7v5l3.5 2" stroke={stroke} strokeWidth={SW} strokeLinecap="round" />
        </>
      );
    // B7 — সাজান.
    case 'sort':
      return <path d="M4 7h16M7 12h10M10 17h4" stroke={stroke} strokeWidth={SW} strokeLinecap="round" />;
    // B7 — ফিল্টার.
    case 'filter':
      return (
        <path d="M3 5h18l-7 8v6l-4 2v-8z" stroke={stroke} strokeWidth={SW} strokeLinejoin="round" />
      );
    // B1 — টাকা পাঠান quick action.
    case 'sendArrow':
      return (
        <path d="M3 12h14M13 7l5 5-5 5" stroke={stroke} strokeWidth={SW} strokeLinecap="round" strokeLinejoin="round" />
      );
    // B1 খাবার · B5 খাবার ও দৈনন্দিন.
    case 'food':
      return (
        <>
          <path d="M4 8h16l-1.5 12h-13z" stroke={stroke} strokeWidth={SW} strokeLinejoin="round" />
          <path d="M8.5 8V6a3.5 3.5 0 017 0v2" stroke={stroke} strokeWidth={SW} />
        </>
      );
    // B1 সার্ভিস — with the dot below.
    case 'service':
      return (
        <>
          <path d="M6 3.5v6a6 6 0 0012 0v-6" stroke={stroke} strokeWidth={SW} strokeLinecap="round" />
          <path d="M12 15.5v5" stroke={stroke} strokeWidth={SW} />
          <circle cx="12" cy="21" r="1.4" fill={stroke} />
        </>
      );
    // B5 হোম ও লোকাল সার্ভিস — the same glyph without the dot, as drawn.
    case 'serviceDomain':
      return (
        <>
          <path d="M6 3.5v6a6 6 0 0012 0v-6" stroke={stroke} strokeWidth={SW} strokeLinecap="round" />
          <path d="M12 15.5v5" stroke={stroke} strokeWidth={SW} />
        </>
      );
    // B5 স্বাস্থ্য ও যত্ন.
    case 'pin':
      return (
        <path
          d="M12 21s-7-4.5-7-10a7 7 0 0114 0c0 5.5-7 10-7 10z"
          stroke={stroke}
          strokeWidth={SW}
          strokeLinejoin="round"
        />
      );
    // EX1 শিক্ষা ও দক্ষতা.
    case 'education':
      return (
        <>
          <path d="M3 8l9-4 9 4-9 4z" stroke={stroke} strokeWidth={SW} strokeLinejoin="round" />
          <path d="M7 11v5c0 1.5 2.2 2.5 5 2.5s5-1 5-2.5v-5" stroke={stroke} strokeWidth={SW} />
        </>
      );
    // EX1 কাজ ও ব্যবসা.
    case 'work':
      return (
        <>
          <rect x="3" y="7" width="18" height="13" rx="1.5" stroke={stroke} strokeWidth={SW} />
          <path d="M9 7V5h6v2" stroke={stroke} strokeWidth={SW} />
        </>
      );
    // EX1 জীবন, পরিবার ও বিনোদন.
    case 'family':
      return (
        <>
          <circle cx="9" cy="8" r="3.2" stroke={stroke} strokeWidth={SW} />
          <circle cx="16" cy="9" r="2.4" stroke={stroke} strokeWidth={SW} />
          <path d="M3.5 20c0-3.2 2.5-5 5.5-5s5.5 1.8 5.5 5" stroke={stroke} strokeWidth={SW} strokeLinecap="round" />
        </>
      );
    // EX1 সরকারি, আইন ও প্রয়োজনীয়.
    case 'government':
      return (
        <path
          d="M4 20h16M6 20V9m12 11V9M4 9l8-5 8 5z"
          stroke={stroke}
          strokeWidth={SW}
          strokeLinejoin="round"
        />
      );
  }
}

export function Glyph({ name, size, stroke, className }: GlyphProps): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
      className={className}
    >
      {paths(name, stroke)}
    </svg>
  );
}
